import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import NotFoundPage from "@saleor/components/NotFoundPage";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { MallErrorFragment } from "@saleor/fragments/types/MallErrorFragment";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { SmopStatusEnum, SmopUserTypeCode } from "@saleor/types/globalTypes";
import { hasNoError, useValidation } from "@saleor/utils/validators/malls";
import React, { useState } from "react";

import {
  MallDetails_mall,
  MallDetails_mall_mallShippingMethod
} from "../../types/MallDetails";
import MallInfo from "../MallInfo";

const timeWithoutSeconds = time_str => {
  const time_array = time_str.split(":");
  return time_array.slice(0, 2).join(":");
};

export interface MallUpdatePageFormData {
  code: string;
  contactPerson: string;
  email: string;
  expressDeliveryAvailable: boolean;
  expressLongDistanceAvailable: boolean;
  foodAndBevCutoffTime: string;
  genMerchCutoffTime: string;
  groceryCutoffTime: string;
  isActive: boolean;
  isServiceable: boolean;
  isInMallPickup: boolean;
  isNationwideDelivery: boolean;
  latitude: string;
  longitude: string;
  mallClosing: string;
  mallOpening: string;
  mobileNo: string;
  name: string;
  sameDayGmCutoffTime: string;
  nextDayAvailable: boolean;
  sameDayAvailable: boolean;
  serviceArea: string;
  smMarketExternalId: number;
  smMarketExternalCode: string;
  smMarketExternalName: string;
  totalLandArea: string;
  telephoneNo: string;
  id: string;
  mallShippingMethod: MallDetails_mall_mallShippingMethod[];
  city: string;
  provinceCode: string;
  provinceName: string;
}

export interface MallDetailsPageProps {
  /** Data */
  mallDetails: MallDetails_mall;
  errors: MallErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;

  /** Form State / Flags */
  disabled: boolean;
  hasManageMallPermission: boolean;

  /** State setters */
  setOpenHistoryDialog: React.Dispatch<React.SetStateAction<boolean>>;

  /** Callbacks */
  onBack: () => void;
  onDelete: () => void;
  onDeactivate: () => void;
  onRefetchMall: () => void;
  onSubmit: (data: MallUpdatePageFormData) => void;
}

const MallUpdatePage: React.FC<MallDetailsPageProps> = ({
  mallDetails,
  errors,
  saveButtonBarState,
  disabled,
  hasManageMallPermission,
  setOpenHistoryDialog,
  onBack,
  onDelete,
  onDeactivate,
  onRefetchMall,
  onSubmit
}) => {
  const user = useUser();

  const initialForm: MallUpdatePageFormData = {
    city: mallDetails?.city || "",
    code: mallDetails?.code || "",
    contactPerson: mallDetails?.contactPerson || "",
    email: mallDetails?.email || "",
    expressDeliveryAvailable:
      mallDetails && mallDetails.expressDeliveryAvailable ? true : false,
    expressLongDistanceAvailable:
      mallDetails && mallDetails.expressLongDistanceAvailable ? true : false,
    foodAndBevCutoffTime: maybe(
      () => timeWithoutSeconds(mallDetails.foodAndBevCutoffTime),
      ""
    ),
    genMerchCutoffTime: maybe(
      () => timeWithoutSeconds(mallDetails.genMerchCutoffTime),
      ""
    ),
    groceryCutoffTime: maybe(
      () => timeWithoutSeconds(mallDetails.groceryCutoffTime),
      ""
    ),

    id: mallDetails?.id || null,

    isActive:
      mallDetails && mallDetails.status === SmopStatusEnum.ACT ? true : false,
    isInMallPickup: mallDetails && mallDetails.isInMallPickup,

    isNationwideDelivery: mallDetails && mallDetails.isNationwideDelivery,
    isServiceable:
      mallDetails &&
      mallDetails.status === SmopStatusEnum.ACT &&
      mallDetails.isServiceable
        ? true
        : false,
    latitude: mallDetails?.latitude || "",
    longitude: mallDetails?.longitude || "",
    mallClosing: maybe(() => timeWithoutSeconds(mallDetails.mallClosing), ""),
    mallOpening: maybe(() => timeWithoutSeconds(mallDetails.mallOpening), ""),

    mallShippingMethod: mallDetails?.mallShippingMethod || null,

    mobileNo: mallDetails?.mobileNo || "",
    name: mallDetails?.name || "",
    nextDayAvailable:
      mallDetails && mallDetails.nextDayAvailable ? true : false,
    provinceCode: mallDetails?.provinceCode || "",
    provinceName: mallDetails?.provinceName || "",
    sameDayAvailable:
      mallDetails && mallDetails.sameDayAvailable ? true : false,
    sameDayGmCutoffTime: maybe(
      () => timeWithoutSeconds(mallDetails.sameDayGmCutoffTime),
      ""
    ),
    serviceArea: mallDetails?.serviceArea || "",

    smMarketExternalCode: mallDetails?.smMarketExternalCode || "",
    smMarketExternalId: mallDetails?.smMarketExternalId,
    smMarketExternalName: mallDetails?.smMarketExternalName || "",
    telephoneNo: mallDetails?.telephoneNo || "",
    totalLandArea: mallDetails?.totalLandArea || ""
  };

  // Form Validation
  const initialFormValidation = {
    city: null,
    code: null,
    contactPerson: null,
    email: null,
    latitude: null,
    longitude: null,
    mallClosing: null,
    mallOpening: null,
    mobileNo: null,
    name: null,
    provinceCode: null,
    serviceArea: null,
    smMarketExternalCode: null,
    smMarketExternalId: null,
    smMarketExternalName: null,
    telephoneNo: null,
    totalLandArea: null
  };
  const [clientErrors, setClientErrors] = useState(initialFormValidation);
  const [resetSubmit, setResetSubmit] = useState(false);
  const handleSubmit = event => {
    const validate = useValidation(event);
    if (hasNoError(validate)) {
      setClientErrors(validate);
      setResetSubmit(true);
      onSubmit(event);
    } else {
      setClientErrors(validate);
      setResetSubmit(false);
    }
  };

  if (mallDetails === null) {
    return <NotFoundPage onBack={onBack} />;
  } else {
    return (
      <Form
        initial={initialForm}
        resetOnSubmit={resetSubmit}
        onSubmit={handleSubmit}
        confirmLeave
      >
        {({ data, change, submit, hasChanged, reset }) => {
          const handleSubmitReset = () => {
            submit();
            if (saveButtonBarState === "success") {
              reset();
            }
          };
          return (
            <>
              <MallInfo
                disabled={disabled}
                data={data}
                clientErrors={clientErrors}
                errors={errors}
                onChange={change}
                onDeactivate={onDeactivate}
                onRefetchMall={onRefetchMall}
                setOpenHistoryDialog={setOpenHistoryDialog}
                hasManageMallPermission={hasManageMallPermission}
              />
              {user?.user?.userType?.code !== SmopUserTypeCode.MARKETING && (
                <SaveButtonBar
                  onDelete={onDelete}
                  onCancel={onBack}
                  onSave={handleSubmitReset}
                  state={saveButtonBarState}
                  disabled={!hasChanged}
                  disableDelete={!hasManageMallPermission}
                />
              )}
            </>
          );
        }}
      </Form>
    );
  }
};

export default MallUpdatePage;
